<template>
  <ContextMenu v-if="brandconfig.CURRENCY_OPTION" @update:open="isOpen = $event">
    <ContextMenuButtonTrigger v-bind="$attrs">
      <button
        type="button"
        class="flex gap-1 items-center h-11"
        aria-haspopup="menu"
        :aria-expanded="isOpen"
        :aria-label="`${$t('Choose your currency')}: ${currencyName}`">
        <IcOutlineEuro v-if="userStore.selectedCurrency === 'EUR'" class="size-5 flex-none" aria-hidden="true" />
        <span
          v-else
          :class="
            clsx(
              {
                'text-[10px]': currencySymbol?.length == 2,
                'text-[8px]': currencySymbol?.length && currencySymbol.length >= 3,
              },
              'text-base font-semibold bg-surface rounded-full text-black size-5 leading-5'
            )
          ">
          {{ currencySymbol }}
        </span>
        <span class="whitespace-nowrap hidden lg:block text-sm font-medium">{{ currencyName }}</span>
        <ChevronDownIcon class="size-3 flex-none transition-transform" :class="isOpen && 'rotate-180'" />
      </button>
    </ContextMenuButtonTrigger>

    <ContextMenuContent class="min-w-64 max-h-80" :aria-label="$t('Choose your currency')">
      <ContextMenuLabel>{{ $t("Choose your currency") }}</ContextMenuLabel>
      <!-- One of a set, so menuitemradio: only that role may carry aria-checked. Picking the current
           one again just closes the menu; `disabled` would grey out the ticked row. -->
      <ContextMenuButton
        v-for="currency of popularFxRates()"
        :key="currency[0]"
        role="menuitemradio"
        class="gap-2"
        :class="currency[0] === userStore.selectedCurrency && 'bg-surface-selected'"
        :aria-checked="currency[0] === userStore.selectedCurrency"
        @select="currency[0] !== userStore.selectedCurrency && selectCurrency(currency[0])">
        <span class="font-medium">{{ currency[3] ? currency[3] : "" }}</span>
        <span class="grow">{{ currency[2] ? currency[2] : currency[0] }}</span>
        <CheckIcon v-if="currency[0] === userStore.selectedCurrency" class="size-5 flex-none" aria-hidden="true" />
      </ContextMenuButton>
      <template v-if="!showAll">
        <ContextMenuSeparator />
        <!-- Keeps the menu open: it only grows the list above. -->
        <ContextMenuButton :close-on-select="false" @select="showAll = true">
          {{ $t("Show all currencies") }}...
        </ContextMenuButton>
      </template>
    </ContextMenuContent>
  </ContextMenu>
  <CurrencyChangeModal :open="showModal" :currency="selectedCurrency" @hide-modal="showModal = false" />
</template>

<script setup lang="ts">
import { ref } from "vue";
import { CheckIcon, ChevronDownIcon } from "@heroicons/vue/24/outline";
import IcOutlineEuro from "~icons/ic/outline-euro";
import {
  ContextMenu,
  ContextMenuButton,
  ContextMenuButtonTrigger,
  ContextMenuContent,
  ContextMenuLabel,
  ContextMenuSeparator,
} from "@xs2event/js-lib/components";
import brandconfig from "@/brandconfig";
import { useUserStore } from "@/store/user";
import CurrencyChangeModal from "./CurrencyChangeModal.vue";
import clsx from "clsx";
import { useCurrencySelect } from "./useCurrencySelect";

// Attributes (a class, a click listener) go to the trigger: the menu itself renders no root element.
defineOptions({ inheritAttrs: false });

const isOpen = ref(false);

const userStore = useUserStore();
const { showModal, selectedCurrency, showAll, popularFxRates, currencySymbol, currencyName, selectCurrency } =
  useCurrencySelect();
</script>
